import React from 'react';
import PropTypes from 'prop-types';
import { Redirect, useLocation } from 'react-router-dom';
import { useShellAppContext } from '../features/shell/context/useShellAppContext';
import { pluginAppInIframe } from '../util/appUtils';

/**
 * ShellSessionGate — bootstrap-aware session gate for the normal app shell.
 *
 * Sits between ShellAppContextProvider and ShellWizardGate in AppLayout.
 * The provider exposes `gatewayClient=null` when the shell session is absent or
 * was already expired at mount time.  Without this gate, pages would silently
 * receive a null gateway client and render blank or partially.
 *
 * Gate behaviour:
 *   - In iframe mode + session missing (gatewayClient=null) →
 *     silently redirect to /embedded/app?returnTo=<currentPath> so the bootstrap
 *     sequence re-runs automatically and the operator returns to the intended
 *     route without seeing a manual-retry wall.
 *   - In standalone / non-iframe mode → always passes through.
 *     A null gateway client in standalone is expected: the app may use the
 *     legacy wsClient fallback and needs no gateway session.
 *
 * Auth-expiry during active session is handled by ShellAppContextProvider's
 * onAuthFailure path, which also triggers a silent rebootstrap redirect.
 */
export function ShellSessionGate({ children }) {
  const { gatewayClient, routeShopId } = useShellAppContext();
  const location = useLocation();
  const routeMatch = location.pathname.match(/^\/app\/([^/]+)(?:\/|$)/);
  let locationShopId = null;
  try {
    locationShopId = routeMatch ? decodeURIComponent(routeMatch[1]) : null;
  } catch (error) {
    // A malformed route is never allowed to inherit an otherwise valid session.
    locationShopId = null;
  }
  const embeddedSessionMatchesRoute = Boolean(
    gatewayClient
    && routeShopId
    && locationShopId
    && String(routeShopId) === String(locationShopId),
  );

  if (pluginAppInIframe() && !embeddedSessionMatchesRoute) {
    const returnTo = location.pathname + location.search;
    return (
      <Redirect to={`/embedded/app?returnTo=${encodeURIComponent(returnTo)}`} />
    );
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}

ShellSessionGate.propTypes = {
  children: PropTypes.node.isRequired,
};
